// Adapted from davinci-elections apps/web/src/theme/system.ts @ 6e23f58
import { createSystem, defaultConfig, defineSemanticTokens, defineTextStyles, defineTokens } from '@chakra-ui/react'
import { COMPACT } from './media'
import { DEFAULT_PRIMARY_COLOR, PAGE_BACKGROUNDS, SURFACE_BACKGROUNDS } from './colors'
import { generateBrandSemanticTokens, generatePaletteScale, normalizeHexColor } from './palette'
import { recipes, slotRecipes } from './recipes'
import semanticTokens from './semantic'
import tokens from './tokens'

/** Rem height of every default-size control on the member pages (48 px touch targets, spec Goal 7). */
export const MEMBER_CONTROL_HEIGHT = '3rem'
/** vocdoni-app's control height (40 px), used in the admin area. */
export const ADMIN_CONTROL_HEIGHT = '2.5rem'

/**
 * Base page styles: vocdoni-app's 16 px Inter body, visible focus rings and reduced motion. `--ipc-control-h` is the
 * minimum height of default-size buttons, inputs and checkboxes: 48 px for members; the admin area
 * (`html[data-area='admin']`, set by `useAdminArea`) gets vocdoni-app's compact 40 px controls and 12/14 px small text.
 */
export const globalCss = {
  html: { '--ipc-control-h': MEMBER_CONTROL_HEIGHT },
  // Small buttons (`sm`, `xs`) are tap targets too on the member pages: 48 px there; the admin area keeps Chakra's.
  "html:not([data-area='admin'])": { '--ipc-control-h-sm': MEMBER_CONTROL_HEIGHT },
  // `!important`: the global styles live in Chakra's `base` cascade layer, which comes before the `tokens` layer that
  // declares the font-size variables, so a plain declaration here would lose to it.
  "html[data-area='admin']": {
    '--ipc-control-h': ADMIN_CONTROL_HEIGHT,
    '--chakra-font-sizes-xs': '0.75rem !important',
    '--chakra-font-sizes-sm': '0.875rem !important',
  },
  body: {
    fontSize: 'md',
    lineHeight: 1.5,
    fontWeight: 400,
    // `viewport-fit=cover` (index.html) lets the page use the whole screen of a phone with a notch; these keep the
    // content clear of the notch in landscape (0 everywhere else).
    paddingLeft: 'env(safe-area-inset-left, 0px)',
    paddingRight: 'env(safe-area-inset-right, 0px)',
  },
  // The member area's bottom tab bar below `lg`: a field the browser scrolls into view on focus stays clear of it.
  // The registration screens (`html[data-app-shell]`) scroll inside their own scroller and need none.
  '@media screen and (max-width: 63.99rem), screen and (max-height: 31.24rem)': {
    'html:not([data-app-shell])': { scrollPaddingBottom: '7rem' },
  },
  // The app shell of the member screens (components/member/layout.tsx, design spec 2026-10-30 §2.1 and §7): on a phone
  // the document never scrolls (the shell's scroller does), so the browser's pull-to-refresh never fires mid-step.
  [COMPACT]: {
    'html[data-app-shell], html[data-app-shell] body': { height: '100%', overflow: 'hidden' },
  },
  // Native feel: no grey flash on tap, no double-tap zoom delay, and a visible pressed state instead (a tint of the
  // control's own text colour, so it shows on solid, outline and ghost buttons alike, in both modes).
  'html[data-app-shell]': { WebkitTapHighlightColor: 'transparent' },
  'html[data-app-shell] :is(a[href], button, [role=button], summary, label)': { touchAction: 'manipulation' },
  'html[data-app-shell] :is(a[href], button, [role=button]):active:not(:disabled, [aria-disabled=true])': {
    backgroundImage:
      'linear-gradient(color-mix(in srgb, currentColor 12%, transparent), color-mix(in srgb, currentColor 12%, transparent))',
  },
  // Visible keyboard focus everywhere (2 px ring, 2 px offset), as in vocdoni-app. The accent's `focusRing` step: at
  // least 3:1 on every surface of both modes (brand.600 was 2.3:1 on the dark page).
  ':focus-visible': {
    outline: '2px solid {colors.brand.focusRing}',
    outlineOffset: '2px',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '*, *::before, *::after': {
      transitionDuration: '0.01ms !important',
      animationDuration: '0.01ms !important',
      animationIterationCount: '1 !important',
      scrollBehavior: 'auto !important',
    },
  },
}

// `xs` and `sm` are 16 px on the member pages: a unitless line height keeps wrapped helper text readable at both scales.
const textStyles = defineTextStyles({
  xs: { value: { fontSize: 'xs', lineHeight: '1.5' } },
  sm: { value: { fontSize: 'sm', lineHeight: '1.5' } },
})

/**
 * Builds the chakra system for a given primary/accent color.
 *
 * Without a color the accent is DEFAULT_PRIMARY_COLOR (Vocdoni deep green): the
 * `brand` scale and its semantic slots are generated from the color and `brand`
 * is the global palette, so buttons, checkboxes, tabs, focus rings and every
 * accent token follow it. Dark-mode surfaces live on
 * the separate `ink` scale and are unaffected either way.
 */
export const createAppSystem = (primaryColor?: string) => {
  const brand = normalizeHexColor(primaryColor) ?? DEFAULT_PRIMARY_COLOR
  const scale = generatePaletteScale(brand)
  const brandTokens = Object.fromEntries(Object.entries(scale).map(([step, value]) => [step, { value }]))
  const brandSemantic = Object.fromEntries(
    Object.entries(generateBrandSemanticTokens(scale, PAGE_BACKGROUNDS, SURFACE_BACKGROUNDS)).map(([slot, value]) => [
      slot,
      { value },
    ])
  )

  return createSystem(defaultConfig, {
    globalCss: { ...globalCss, html: { ...globalCss.html, colorPalette: 'brand' } },
    theme: {
      tokens: defineTokens({
        ...tokens,
        colors: { ...tokens.colors, brand: brandTokens },
      }),
      semanticTokens: defineSemanticTokens({
        ...semanticTokens,
        colors: { ...semanticTokens.colors, brand: brandSemantic },
      }),
      textStyles,
      recipes,
      slotRecipes,
    },
  })
}

export type AppSystem = ReturnType<typeof createAppSystem>

// Systems are memoized per color: building one is not free and the color is
// constant for a process (server) or a page load (client), so the provider can
// call this on every render without paying for it.
const systems = new Map<string, AppSystem>()

export const getAppSystem = (primaryColor?: string): AppSystem => {
  const key = normalizeHexColor(primaryColor) ?? ''
  let cached = systems.get(key)
  if (!cached) {
    cached = createAppSystem(key || undefined)
    systems.set(key, cached)
  }
  return cached
}

// The stock system (no PRIMARY_COLOR). Also what `chakra typegen` reads.
export const system = getAppSystem()
